import React, {
	createContext,
	type ReactNode,
	useContext,
	useMemo,
} from 'react';
import { useInterpret } from '@xstate/react';
import type { AnyInterpreter } from 'xstate';

import type { CustomLayoutServiceContext } from 'lib/page-props';
import {
	authMachine,
	type AuthMachineActor,
	userMachine,
	type UserMachineActor,
} from 'state-machines/authentication';
import { cartMachine, CartMachineActor } from 'state-machines/cart';
import {
	wishlistMachine,
	type WishlistMachineActor,
} from 'state-machines/wishlist';

import { useGlobalMemberPopoversContext } from './GlobalMemberPopoversContextProvider';
import { useGlobalPopoversContext } from './GlobalPopoversContextProvider';

interface GlobalMachinesContextType {
	authService: AuthMachineActor;
	cartService: CartMachineActor;
	userService: UserMachineActor;
	wishlistService: WishlistMachineActor;
}

export const GlobalMachinesContext = createContext<GlobalMachinesContextType>(
	{} as any,
);

interface Props {
	children: ReactNode;
	sitecoreContext: CustomLayoutServiceContext;
}

export function GlobalMachinesContextProvider({
	children,
	sitecoreContext,
}: Props) {
	const {
		closeLogin,
		openJulaClubLogin,
		openJulaProLogin,
		openSignUpJulaClub,
		openSignUpJulaPro,
		openUserInformationValidation,
	} = useGlobalMemberPopoversContext();

	const { openContactForm } = useGlobalPopoversContext();

	const isEditing = sitecoreContext?.pageEditing;
	const globalLinks = sitecoreContext?.globalLinks;
	const userService = useInterpret(userMachine, {
		context: {
			skipAuth: isEditing,
			organizationDetailUrl: globalLinks?.accountDetailsPro || '',
		},
		devTools: true,
	});
	const authService = useInterpret(authMachine, {
		devTools: true,
		actions: {
			openLoginPopoverClubStaff: () => openJulaClubLogin(),
			openLoginPopoverPro: () => openJulaProLogin(),
			closeLoginPopover: () => closeLogin(),
			openJulaProSignupForm: () => openSignUpJulaPro(),
			openJulaClubSignupForm: () => openSignUpJulaClub(),
			openCustomerContactForm: () => openContactForm(),
			openInformationValidationPopover: () => openUserInformationValidation(),
		},
	});
	const cartService = useInterpret(cartMachine, { devTools: true });

	const wishlistService = useInterpret(wishlistMachine, {
		devTools: true,
	});

	const globalMachinesContextValue: Record<
		keyof GlobalMachinesContextType,
		AnyInterpreter
	> = useMemo(
		() => ({
			authService,
			userService,
			cartService,
			wishlistService,
		}),
		[authService, userService, cartService, wishlistService],
	);
	return (
		<GlobalMachinesContext.Provider value={globalMachinesContextValue}>
			{children}
		</GlobalMachinesContext.Provider>
	);
}
GlobalMachinesContextProvider.displayName = 'GlobalMachinesContextProvider';

export function useGlobalMachinesContext() {
	return useContext(GlobalMachinesContext);
}
